import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  fmtTime, gameNameById, teamLabel,
  PlayersBadge, StatPill, GameFilterDropdown, ScoreboardBackground
} from "./ScoreboardShared";

const API = process.env.REACT_APP_API_URL;

function withinDays(date, days) {
  if (!date) return false;
  const d = new Date(date).getTime();
  const now = Date.now();
  return now - d <= days * 24 * 60 * 60 * 1000;
}

function makeSessionMetricsFromSummary(s, summaryForSession = {}) {
  const played = s.playedDuration || 0;

  const l1 = Number(summaryForSession?.hints_l1 ?? 0) || 0;
  const l2 = Number(summaryForSession?.hints_l2 ?? 0) || 0;
  const l3 = Number(summaryForSession?.hints_l3 ?? 0) || 0;
  const answer = Number(summaryForSession?.hints_answer ?? 0) || 0;

  const hintsTotal = l1 + l2 + l3 + answer;

  const errors = Number(summaryForSession?.errors ?? 0) || 0;

  // ✅ VRAIS POINTS (DB) si disponibles
  const realPoints = typeof s.points === "number" ? s.points : null;

  // fallback si ancienne session pas migrée / pas de points en DB
  const timeScore = Math.max(0, 6000 - Math.floor(played * 6));
  const penaltyHints = l1 * 40 + l2 * 90 + l3 * 160 + answer * 220;
  const penaltyErrors = errors * 120;
  const fallbackPoints = Math.max(0, Math.round(timeScore - penaltyHints - penaltyErrors));

  return {
    hints: { l1, l2, l3, answer, total: hintsTotal },
    errors,
    points: realPoints != null ? realPoints : fallbackPoints,
  };
}

export default function ScoreboardsHub() {
  // accès scoreboards (via code)
  const [sessionCode, setSessionCode] = useState("");
  const [companyCode, setCompanyCode] = useState("");
  // filtres
  const [period, setPeriod] = useState("all"); // all | week | month
  const [gameFilter, setGameFilter] = useState("all"); // all | "1" | etc
  // data
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  // metrics réelles par sessionId
  const [metricsBySessionId, setMetricsBySessionId] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;

    setError(null);
    setData(null);
    setMetricsBySessionId(null);

    const qs = gameFilter === "all" ? "" : `?gameId=${gameFilter}`;

    (async () => {
      try {
        const res = await axios.get(`${API}/api/scoreboards/global${qs}`);
        if (cancelled) return;

        const payload = res.data;
        setData(payload);

        const sessions = payload?.sessions || [];
        const ids = sessions.map((s) => s.id).filter(Boolean);

        if (ids.length === 0) {
          setMetricsBySessionId({});
          return;
        }

        // On récupère les compteurs
        const metricsRes = await axios.get(`${API}/api/metrics/summary`, {
          params: {
            sessionIds: ids.join(","),
            metricKeys: "errors,hints_l1,hints_l2,hints_l3,hints_answer",
          },
        });

        if (cancelled) return;
        setMetricsBySessionId(metricsRes.data?.bySessionId || {});
      } catch (e) {
        if (cancelled) return;
        setError(e.response?.data?.message || "Erreur chargement global");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [gameFilter]);

  const sessionsRanked = useMemo(() => {
    const sessions = data?.sessions || [];

    const periodFiltered =
      period === "week"
        ? sessions.filter((s) => withinDays(s.finishedAt, 7))
        : period === "month"
          ? sessions.filter((s) => withinDays(s.finishedAt, 30))
          : sessions;

    const enriched = periodFiltered.map((s, idx) => {
      const sid = String(s.id);
      const summary = metricsBySessionId?.[sid] || {};
      const metrics = makeSessionMetricsFromSummary(s, summary);

      return {
        ...s,
        _metrics: metrics,
        _key: `${idx}-${s.finishedAt || ""}-${s.playedDuration || 0}-${teamLabel(s)}`,
      };
    });

    enriched.sort((a, b) => {
      const pa = a._metrics?.points ?? 0;
      const pb = b._metrics?.points ?? 0;
      if (pb !== pa) return pb - pa;
      const ta = a.playedDuration ?? Number.MAX_SAFE_INTEGER;
      const tb = b.playedDuration ?? Number.MAX_SAFE_INTEGER;
      return ta - tb;
    });

    return enriched;
  }, [data, period, metricsBySessionId]);

  const podium = sessionsRanked.slice(0, 3);
  const top = sessionsRanked.slice(3, 23); // commence à la 4e

  const goSession = (e) => {
    e.preventDefault();
    const c = sessionCode.trim().toUpperCase();
    if (!c) return;
    navigate(`/scoreboard/${c}`);
  };

  const goCompany = (e) => {
    e.preventDefault();
    const c = companyCode.trim().toUpperCase();
    if (!c) return;
    navigate(`/scoreboards/company/by-session/${c}`);
  };

  const formulaText = useMemo(() => {
    return [
      "Points = max(0, timeScore − penaltyHints − penaltyErrors)",
      "timeScore = max(0, 6000 − (temps_en_secondes × 6))",
      "penaltyHints = (N1×40) + (N2×90) + (N3×160) + (Réponse×220)",
      "penaltyErrors = erreurs × 120",
      "Tri: points décroissants (puis temps croissant).",
    ];
  }, []);

  return (
    <div className="min-h-[calc(100vh-85px)] bg-[#070712] text-white">
      <ScoreboardBackground />

      <div className="relative max-w-7xl mx-auto px-4 py-10">
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-[0.28em] text-slate-400">
              Blackout Games · Scoreboards
            </p>
            <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">
              Leaderboard global <span className="ml-2 text-fuchsia-300">⚡</span>
            </h1>
          </div>

          <div className="mt-2 md:mt-0 flex flex-col sm:flex-row gap-3 items-start sm:items-center">
            <div className="inline-flex rounded-2xl border border-white/10 bg-white/5 p-1 backdrop-blur">
              {[
                { key: "all", label: "All-time" },
                { key: "week", label: "7 jours" },
                { key: "month", label: "30 jours" },
              ].map((t) => (
                <button
                  key={t.key}
                  onClick={() => setPeriod(t.key)}
                  className={[
                    "px-4 py-2 text-xs font-semibold rounded-xl transition whitespace-nowrap",
                    period === t.key
                      ? "bg-gradient-to-r from-fuchsia-600/80 to-indigo-600/80 shadow-[0_0_0_1px_rgba(255,255,255,0.12)]"
                      : "text-slate-300 hover:text-white hover:bg-white/5",
                  ].join(" ")}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <GameFilterDropdown value={gameFilter} onChange={setGameFilter} />
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* MAIN */}
          <section className="lg:col-span-8 space-y-6">
            {/* podium */}
            <div className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur p-5">
              <div className="flex items-center justify-between">
                <p className="text-[11px] uppercase tracking-[0.22em] text-slate-400">
                  Podium
                </p>
                <p className="text-xs text-slate-400">
                  {period === "all"
                    ? "Classement points (all-time)"
                    : "Classement points (filtré)"}
                </p>
              </div>

              {error && <div className="mt-4 text-sm text-rose-300">❌ {error}</div>}
              {!data && !error && (
                <div className="mt-4 text-sm text-slate-300">Chargement…</div>
              )}

              {data && podium.length === 0 && (
                <div className="mt-4 text-sm text-slate-300">
                  Aucune session terminée pour cette période.
                </div>
              )}

              {podium.length > 0 && (
                <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4">
                  {podium.map((s, idx) => {
                    const rank = idx + 1;
                    const medal = rank === 1 ? "🥇" : rank === 2 ? "🥈" : "🥉";
                    const highlight =
                      rank === 1
                        ? "border-fuchsia-400/40 shadow-[0_0_28px_rgba(217,70,239,0.20)]"
                        : "border-white/10";

                    const m = s._metrics;

                    return (
                      <div
                        key={`podium-${s._key}`}
                        className={["rounded-2xl border bg-black/30 p-4", highlight].join(" ")}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-center gap-2">
                            <span className="text-2xl">{medal}</span>
                            <span className="rounded-xl border border-white/10 bg-white/5 px-2 py-1 text-xs font-extrabold text-slate-200">
                              #{rank}
                            </span>
                          </div>

                          <div className="text-right">
                            <p className="text-lg font-extrabold text-emerald-200 whitespace-nowrap">
                              {m.points} pts
                            </p>
                            <p className="text-[11px] font-semibold text-cyan-200 whitespace-nowrap">
                              {fmtTime(s.playedDuration || 0)}
                            </p>
                          </div>
                        </div>

                        <p className="mt-3 text-base font-extrabold text-slate-100">
                          {teamLabel(s)}
                        </p>

                        <p className="mt-1 text-[11px] text-slate-400 line-clamp-2">
                          {(s.players || []).map((p) => p.pseudo).join(", ")}
                        </p>

                        <div className="mt-3 flex flex-wrap items-center gap-2">
                          <PlayersBadge n={s.nbPlayers} />
                          <span className="text-[11px] text-slate-500">
                            {gameNameById(s.gameId)}
                          </span>

                          <span className="h-1 w-1 rounded-full bg-white/20" />

                          <StatPill label="Indices" value={m.hints.total} kind="hints" />
                          <StatPill label="Réponses" value={m.hints.answer} kind="answers" />
                          <StatPill label="Erreurs" value={m.errors} kind="errors" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* list top */}
            <div className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur p-5">
              <div className="flex items-center justify-between">
                <p className="text-[11px] uppercase tracking-[0.22em] text-slate-400">
                  Top équipes
                </p>
                <p className="text-xs text-slate-400">{top.length ? `(hors podium)` : "—"}</p>
              </div>

              {data && top.length > 0 && (
                <div className="mt-4 space-y-2">
                  {top.map((s, i) => {
                    const m = s._metrics;
                    const rank = i + 4;
                    return (
                      <div
                        key={`row-${s._key}`}
                        className="rounded-2xl border border-white/10 bg-black/30 px-4 py-3 flex items-center justify-between gap-4"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="h-9 w-9 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center font-extrabold">
                            {rank}
                          </div>

                          <div className="min-w-0">
                            <p className="text-sm font-extrabold">{teamLabel(s)}</p>

                            <p className="mt-1 text-[11px] text-slate-400 line-clamp-1">
                              {(s.players || []).map((p) => p.pseudo).join(", ")}
                            </p>

                            <div className="mt-1 flex flex-wrap items-center gap-2">
                              <PlayersBadge n={s.nbPlayers} />
                              <span className="text-[11px] text-slate-500">
                                {gameNameById(s.gameId)}
                              </span>
                              {s.finishedAt ? (
                                <span className="text-[11px] text-slate-500">
                                  · {new Date(s.finishedAt).toLocaleDateString()}
                                </span>
                              ) : null}

                              <span className="h-1 w-1 rounded-full bg-white/20" />

                              <StatPill label="Indices" value={m.hints.total} kind="hints" />
                              <StatPill label="Réponses" value={m.hints.answer} kind="answers" />
                              <StatPill label="Erreurs" value={m.errors} kind="errors" />
                            </div>
                          </div>
                        </div>

                        <div className="flex flex-col items-end gap-1 flex-shrink-0">
                          <div className="text-sm font-extrabold text-emerald-200 whitespace-nowrap">
                            {m.points} pts
                          </div>
                          <div className="text-[11px] font-semibold text-cyan-200 whitespace-nowrap">
                            {fmtTime(s.playedDuration || 0)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {data && top.length === 0 && (
                <p className="mt-4 text-sm text-slate-300">
                  Aucune session terminée hors podium pour le moment.
                </p>
              )}
            </div>
          </section>

          {/* ASIDE */}
          <aside className="lg:col-span-4 space-y-6">
            <div className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur p-5">
              <p className="text-[11px] uppercase tracking-[0.22em] text-slate-400">
                Accès scoreboards
              </p>
              <p className="mt-2 text-sm text-slate-300">
                Pour voir un débrief détaillé, il faut un code de session.
              </p>

              <form className="mt-4 space-y-2" onSubmit={goSession}>
                <label className="text-[11px] text-slate-400 uppercase tracking-[0.18em]">
                  Scoreboard session
                </label>
                <div className="flex gap-2">
                  <input
                    value={sessionCode}
                    onChange={(e) => setSessionCode(e.target.value)}
                    placeholder="Entrez un code de session"
                    className="flex-1 rounded-2xl border border-white/10 bg-black/30 px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-fuchsia-400/60"
                  />
                  <button className="rounded-2xl px-4 py-2 text-sm font-extrabold bg-gradient-to-r from-fuchsia-600/80 to-indigo-600/80 border border-white/10 hover:brightness-110 transition">
                    GO
                  </button>
                </div>
              </form>

              <form className="mt-4 space-y-2" onSubmit={goCompany}>
                <label className="text-[11px] text-slate-400 uppercase tracking-[0.18em]">
                  Scoreboard entreprise
                </label>
                <div className="flex gap-2">
                  <input
                    value={companyCode}
                    onChange={(e) => setCompanyCode(e.target.value)}
                    placeholder="Entrez un code de session"
                    className="flex-1 rounded-2xl border border-white/10 bg-black/30 px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-400/60"
                  />
                  <button className="rounded-2xl px-4 py-2 text-sm font-extrabold bg-gradient-to-r from-cyan-600/70 to-indigo-600/70 border border-white/10 hover:brightness-110 transition">
                    GO
                  </button>
                </div>
              </form>
            </div>
          </aside>
        </div>

        {/* formula */}
        <div className="mt-6 rounded-3xl border border-white/10 bg-white/5 backdrop-blur p-5">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <p className="text-[11px] uppercase tracking-[0.22em] text-slate-400">
                Système de points
              </p>
              <p className="mt-1 text-sm text-slate-300">
                Le classement est basé sur les points. Le temps reste affiché mais secondaire.
              </p>
            </div>

            <details className="rounded-2xl border border-white/10 bg-black/30 px-4 py-3">
              <summary className="cursor-pointer text-sm font-semibold text-slate-200">
                Voir la formule
              </summary>
              <div className="mt-3 text-[13px] text-slate-300 space-y-2">
                {formulaText.map((line, i) => (
                  <p key={i} className="leading-relaxed">
                    • {line}
                  </p>
                ))}
                <p className="text-[12px] text-slate-400 mt-2">
                  (✅ Erreurs + indices + réponses proviennent maintenant des metrics.)
                </p>
              </div>
            </details>
          </div>
        </div>
      </div>
    </div>
  );
}
